
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { AddTopAuctionDialog } from './AddTopAuctionDialog';

interface TopAuction {
  id: string;
  announcement_id: string;
  display_order: number;
  announcements: {
    title: string;
    description: string | null;
  };
}

export const TopAuctionsSection = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: topAuctions, isLoading } = useQuery({
    queryKey: ['admin-top-auctions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('top_auctions')
        .select(`
          id,
          announcement_id,
          display_order,
          announcements!top_auctions_announcement_id_fkey (
            title,
            description
          )
        `)
        .order('display_order');

      if (error) throw error;
      return data as TopAuction[];
    },
  });

  const moveTopAuction = useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: 'up' | 'down' }) => {
      if (!topAuctions) return;
      const currentIndex = topAuctions.findIndex(a => a.id === id);
      if (currentIndex === -1) return;
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= topAuctions.length) return;

      const current = topAuctions[currentIndex];
      const target = topAuctions[targetIndex];

      const { error: e1 } = await supabase
        .from('top_auctions')
        .update({ display_order: target.display_order })
        .eq('id', current.id);
      if (e1) throw e1;

      const { error: e2 } = await supabase
        .from('top_auctions')
        .update({ display_order: current.display_order })
        .eq('id', target.id);
      if (e2) throw e2;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-top-auctions'] });
      toast({ title: "Success", description: "Display order updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update display order.", variant: "destructive" });
    },
  });

  const removeTopAuction = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('top_auctions').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-top-auctions'] });
      toast({ title: "Success", description: "Top auction removed." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to remove top auction.", variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold mb-2">Top Auctions</h2>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold mb-2">Top Auctions</h2>
          <p className="text-muted-foreground">Manage featured auctions shown on the auctions page</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Top Auction
        </Button>
      </div>

      <div className="grid gap-4">
        {topAuctions?.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground">No top auctions selected yet.</p>
            </CardContent>
          </Card>
        ) : (
          topAuctions?.map((auction, index) => (
            <Card key={auction.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">{auction.announcements?.title}</h3>
                    {auction.announcements?.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {auction.announcements.description}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Position: {auction.display_order}</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => moveTopAuction.mutate({ id: auction.id, direction: 'up' })}
                      disabled={index === 0 || moveTopAuction.isPending}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => moveTopAuction.mutate({ id: auction.id, direction: 'down' })}
                      disabled={index === (topAuctions?.length || 0) - 1 || moveTopAuction.isPending}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeTopAuction.mutate(auction.id)}
                      disabled={removeTopAuction.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <AddTopAuctionDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['admin-top-auctions'] });
        }}
      />
    </div>
  );
};
